import {Checkbox} from '@common-web/components/ui/checkbox';
import {Label} from '@common-web/components/ui/label';
import {CheckCircle, XCircle, Settings} from 'lucide-react';

type FeatureFlagDefinition = {
  key: string;
  label: string;
  description: string;
};

//TODO: need to investigate feature flag granularity (clients, users, roles, permissions, etc;) in a more sustainable way. Tracked in: https://app.asana.com/1/1206214889537900/project/1212886114840713/task/1214550113535442?focus=true
const FEATURE_FLAGS: FeatureFlagDefinition[] = [
  {
    key: 'payrollProcessingEnabled',
    label: 'File Validation & Processing',
    description: 'Allow access to group file validation and processing features',
  },
];

export type FeatureFlagsState = Record<string, boolean>;

export function getDefaultFeatureFlags(): FeatureFlagsState {
  return Object.fromEntries(FEATURE_FLAGS.map((f) => [f.key, false]));
}

export function FeatureFlagEditor({
  idPrefix,
  flags,
  onChange,
}: {
  idPrefix: string;
  flags: FeatureFlagsState;
  onChange: (flags: FeatureFlagsState) => void;
}) {
  return (
    <div className='space-y-3 border-t pt-4'>
      <div className='flex items-center gap-2'>
        <Settings className='h-4 w-4 text-brand-gray' />
        <Label className='text-base font-semibold'>Feature Access</Label>
      </div>
      <div className='space-y-3 pl-6'>
        {FEATURE_FLAGS.map((flag) => (
          <div key={flag.key} className='flex items-start space-x-3'>
            <Checkbox
              id={`${idPrefix}-${flag.key}`}
              checked={!!flags[flag.key]}
              onCheckedChange={(checked) => onChange({...flags, [flag.key]: checked === true})}
            />
            <div className='space-y-1'>
              <Label htmlFor={`${idPrefix}-${flag.key}`} className='font-medium cursor-pointer'>
                {flag.label}
              </Label>
              <p className='text-sm text-brand-gray'>{flag.description}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function FeatureFlagDisplay({flags}: {flags: FeatureFlagsState | null | undefined}) {
  return (
    <div className='space-y-4'>
      {FEATURE_FLAGS.map((flag) => (
        <div key={flag.key} className='flex items-start justify-between'>
          <div className='space-y-0.5'>
            <div className='font-medium text-brand-navy'>{flag.label}</div>
            <div className='text-sm text-brand-gray'>{flag.description}</div>
          </div>
          {flags?.[flag.key] ? (
            <CheckCircle className='h-5 w-5 text-emerald shrink-0' />
          ) : (
            <XCircle className='h-5 w-5 text-gray-300 shrink-0' />
          )}
        </div>
      ))}
    </div>
  );
}

export function FeatureFlagEditableCard({
  idPrefix,
  flags,
  onChange,
}: {
  idPrefix: string;
  flags: FeatureFlagsState;
  onChange: (flags: FeatureFlagsState) => void;
}) {
  return (
    <div className='space-y-4'>
      {FEATURE_FLAGS.map((flag) => (
        <div
          key={flag.key}
          className='flex items-start space-x-3 p-2 rounded-lg hover:bg-gray-50 border border-transparent hover:border-gray-100 transition-all'
        >
          <Checkbox
            id={`${idPrefix}-${flag.key}`}
            checked={!!flags[flag.key]}
            onCheckedChange={(checked) => onChange({...flags, [flag.key]: checked === true})}
          />
          <div className='space-y-1'>
            <Label htmlFor={`${idPrefix}-${flag.key}`} className='font-medium cursor-pointer'>
              {flag.label}
            </Label>
            <p className='text-sm text-brand-gray'>{flag.description}</p>
          </div>
        </div>
      ))}
    </div>
  );
}
